import ContentState from "../UI/ContentState";
import { ChangeEvent, FormEvent, Fragment, ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { SERVER_ADDRESS } from "../server";
import { subscribeSupportActivity } from "./supportRealtime";
import { handleChatComposerKeyDown } from "./chatComposerKeyboard";
import sendIcon from "../../Image/Support/send.svg";
import inquirySendIcon from "../../Image/inquiry/send.svg";

export interface SupportAttachment {
  seq: number;
  file_path: string;
  original_name?: string | null;
  mime_type?: string | null;
}

export interface SupportMessage {
  seq: number;
  sender_role: "customer" | "staff";
  is_auto?: boolean;
  sender_name?: string;
  content?: string | null;
  created_at: string;
  attachments: SupportAttachment[];
  edited_at?: string | null;
  deleted_at?: string | null;
  revision_seq?: number;
  can_edit?: boolean;
  inquiry_context?:{action?:string;order_seq?:number}|null;
}

export interface SupportMacro { seq: number; title: string; content: string; tags: string[]; }

interface SupportChatThreadProps {
  conversationSeq: number;
  mineRole: "customer" | "staff";
  loadMessages: (signal?: AbortSignal) => Promise<SupportMessage[]>;
  sendMessage?: (form: FormData) => Promise<void>;
  readOnly?: boolean;
  onActivity?: () => void;
  variant?: "seller" | "customer";
  disabledReason?: string;
  footerAction?: ReactNode;
  draft?: { key: number; content: string };
  refreshKey?: string | number;
  summary?: ReactNode;
  children?: ReactNode;
  composerContext?: ReactNode;
  onSelectProducts?: () => void;
  loadMacros?: (signal: AbortSignal) => Promise<SupportMacro[]>;
  useMacro?: (seq: number) => Promise<string>;
  editMessage?: (seq: number, content: string, revisionSeq: number) => Promise<void>;
  deleteMessage?: (seq: number, revisionSeq: number) => Promise<void>;
}

const attachmentUrl = (path: string) => path.startsWith("http")
  ? path
  : `${SERVER_ADDRESS}openfile/${path.replace(/^\/+/, "")}`;

const isVideo = (attachment: SupportAttachment) => attachment.mime_type?.startsWith("video/")
  || /\.(mp4|webm)(?:$|[?#])/i.test(attachment.file_path);

export default function SupportChatThread({ conversationSeq, mineRole, loadMessages, sendMessage, readOnly = false, onActivity, variant = "customer", disabledReason, footerAction, draft, refreshKey, summary, children, composerContext, onSelectProducts, loadMacros, useMacro: applyMacro, editMessage, deleteMessage }: SupportChatThreadProps) {
  const sequence = useRef(0);
  const messagesRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const followLatestRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const previewsRef = useRef<string[]>([]);
  const loadMessagesRef = useRef(loadMessages);
  const onActivityRef = useRef(onActivity);
  const activityKeyRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [caret, setCaret] = useState(0);
  const [macros, setMacros] = useState<SupportMacro[]>([]);
  const [macroIndex, setMacroIndex] = useState(0);
  const [macroError, setMacroError] = useState("");
  const [macroBusy, setMacroBusy] = useState(false);
  const [dismissedMacro, setDismissedMacro] = useState<string | null>(null);
  const macroUseRef = useRef(false);
  const macroListId = useId();
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = '44px';
    input.style.height = `${Math.min(input.scrollHeight + input.offsetHeight - input.clientHeight, 132)}px`;
    input.style.overflowY = input.scrollHeight > input.clientHeight ? 'auto' : 'hidden';
  }, [text]);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState("");
  const sendingRef = useRef(false);
  const sendVersion = useRef(0);
  const conversationRef = useRef(conversationSeq);
  const refreshKeyRef = useRef({ conversationSeq, key: refreshKey });
  const attachmentRef = useRef<HTMLDivElement>(null);
  const attachmentId = useId();
  const [attachmentMenu, setAttachmentMenu] = useState(false);
  const [editing, setEditing] = useState<{ seq: number; content: string; revision: number } | null>(null);
  const [deleting, setDeleting] = useState<SupportMessage | null>(null);
  const [revisionError, setRevisionError] = useState("");
  const [revisionBusy, setRevisionBusy] = useState(false);
  const revisionBusyRef = useRef(false);
  conversationRef.current = conversationSeq;
  const tagMatch = mineRole === "staff" && loadMacros && applyMacro && !disabledReason && !sending
    ? /(?:^|\s)#([^\s#]*)$/.exec(text.slice(0, caret)) : null;
  const tag = tagMatch?.[1];
  const macroKey = tag === undefined ? null : `${caret}:${tag}`;
  const showMacros = macroKey !== null && macroKey !== dismissedMacro;
  const matchingMacros = macros.filter(macro => macro.tags.some(value => value.toLocaleLowerCase().startsWith((tag || "").toLocaleLowerCase())));
  useEffect(() => {
    setMacros([]); setMacroError(""); setMacroIndex(0);
    if (!showMacros || !loadMacros) return;
    const abort = new AbortController();
    void loadMacros(abort.signal).then(items => { if (!abort.signal.aborted) setMacros(items); })
      .catch(() => { if (!abort.signal.aborted) setMacroError("매크로를 불러오지 못했습니다. 태그를 다시 입력해주세요."); });
    return () => abort.abort();
  }, [showMacros, tag, loadMacros, conversationSeq]);

  const insertMacro = async (macro: SupportMacro) => {
    if (!applyMacro || tag === undefined || macroUseRef.current || sendingRef.current || disabledReason) return;
    const version = sendVersion.current, target = conversationSeq, end = caret, start = end - tag.length - 1;
    macroUseRef.current = true; setMacroBusy(true); setMacroError("");
    try {
      const content = await applyMacro(macro.seq);
      if (version !== sendVersion.current || target !== conversationRef.current) return;
      const next = text.slice(0, start) + content + text.slice(end);
      if (!content || next.length > 2000) throw new Error("매크로를 넣으면 최대 2,000자를 넘습니다. 작성 내용을 줄여주세요.");
      setText(next); setCaret(start + content.length); setDismissedMacro(macroKey);
      requestAnimationFrame(() => { inputRef.current?.focus(); inputRef.current?.setSelectionRange(start + content.length, start + content.length); });
    } catch (cause: any) {
      if (version === sendVersion.current && target === conversationRef.current) setMacroError(cause?.response?.data?.message || cause?.message || "매크로를 넣지 못했습니다.");
    } finally {
      if (version === sendVersion.current && target === conversationRef.current) { macroUseRef.current = false; setMacroBusy(false); }
    }
  };


  useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);
  useEffect(() => { onActivityRef.current = onActivity; }, [onActivity]);
  useEffect(() => { if (draft && !sendingRef.current) setText(draft.content); }, [draft]);
  useEffect(() => {
    if (!attachmentMenu) return;
    const close = (event: PointerEvent) => { if (!attachmentRef.current?.contains(event.target as Node)) setAttachmentMenu(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setAttachmentMenu(false); };
    document.addEventListener("pointerdown", close); document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", escape); };
  }, [attachmentMenu]);

  const refresh = useCallback(async (showLoading = false, signal?: AbortSignal) => {
    const current = ++sequence.current;
    if (showLoading) setLoading(true);
    try {
      const next = await loadMessagesRef.current(signal);
      if (signal?.aborted || current !== sequence.current) return;
      if (!Array.isArray(next)) throw new Error("Invalid messages");
      setMessages(next);
      setError("");
      const lastMessage = next[next.length - 1];
      const activityKey = `${conversationSeq}:${lastMessage?.seq ?? "empty"}`;
      if (activityKeyRef.current !== activityKey) {
        activityKeyRef.current = activityKey;
        onActivityRef.current?.();
      }
    } catch (err: any) {
      if (signal?.aborted || current !== sequence.current) return;
      setError(err?.response?.data?.message || "대화 내용을 불러오지 못했습니다.");
    } finally {
      if (!signal?.aborted && current === sequence.current) setLoading(false);
    }
  }, [conversationSeq]);

  useEffect(() => {
    const controller = new AbortController();
    activityKeyRef.current = null;
    setMessages([]);
    setText(""); setFiles([]); setPreviews([]); setComposeError(""); setSending(false);
    setCaret(0); setDismissedMacro(null); setMacroBusy(false); macroUseRef.current = false;
    setEditing(null); setDeleting(null); setRevisionError(""); setRevisionBusy(false); revisionBusyRef.current = false;
    sendingRef.current = false;
    previewsRef.current.forEach(preview => URL.revokeObjectURL?.(preview));
    void refresh(true, controller.signal);
    const timer = window.setInterval(() => void refresh(false), 30000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      sequence.current += 1;
      sendVersion.current += 1;
    };
  }, [conversationSeq, refresh]);

  useEffect(() => subscribeSupportActivity(() => { void refresh(false); }), [refresh]);

  useEffect(() => {
    const previous = refreshKeyRef.current;
    refreshKeyRef.current = { conversationSeq, key: refreshKey };
    if (previous.conversationSeq === conversationSeq && previous.key !== refreshKey) void refresh(false);
  }, [conversationSeq, refreshKey, refresh]);

  const scrollToLatest = useCallback(() => {
    const container = messagesRef.current;
    if (container && followLatestRef.current) {
      container.scrollTop = container.scrollHeight;
      lastScrollTopRef.current = container.scrollTop;
    }
  }, []);

  useLayoutEffect(() => { followLatestRef.current = true; lastScrollTopRef.current = 0; }, [conversationSeq]);
  useLayoutEffect(() => { if (!loading) scrollToLatest(); }, [loading, messages, children, disabledReason, scrollToLatest]);
  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    // Attachments, reply cards and the composer can change height after messages load.
    const observer = new ResizeObserver(scrollToLatest);
    if (messagesRef.current) observer.observe(messagesRef.current);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [scrollToLatest]);

  useEffect(() => { previewsRef.current = previews; }, [previews]);
  useEffect(() => () => previewsRef.current.forEach(url => URL.revokeObjectURL?.(url)), []);

  const chooseFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (sendingRef.current || disabledReason) return;
    if (!selected.length) return;
    if (selected.length + files.length > 5) { setComposeError("사진과 동영상은 한 번에 최대 5개까지 첨부할 수 있습니다."); return; }
    if (selected.some(file => !["image/jpeg", "image/png", "image/webp", "video/mp4", "video/webm"].includes(file.type))) {
      setComposeError("JPG, PNG, WebP 사진과 MP4, WebM 동영상을 첨부해주세요."); return;
    }
    if (selected.some(file => file.size > (file.type.startsWith("video/") ? 50 : 10) * 1024 * 1024)) {
      setComposeError("사진은 10MB, 동영상은 50MB 이하만 첨부할 수 있습니다."); return;
    }
    setComposeError("");
    setFiles(previous => [...previous, ...selected]);
    setPreviews(previous => [...previous, ...selected.map(file => URL.createObjectURL(file))]);
  };

  const removeFile = (index: number) => {
    if (sendingRef.current) return;
    URL.revokeObjectURL?.(previews[index]);
    setFiles(previous => previous.filter((_, itemIndex) => itemIndex !== index));
    setPreviews(previous => previous.filter((_, itemIndex) => itemIndex !== index));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (readOnly || !sendMessage || sendingRef.current || macroUseRef.current || disabledReason || (!text.trim() && files.length === 0 && !composerContext)) return;
    const targetConversation = conversationSeq;
    const version = sendVersion.current;
    sendingRef.current = true;
    setSending(true);
    setComposeError("");
    const form = new FormData();
    if (text.trim()) form.append("content", text.trim());
    files.forEach(file => form.append("files", file));
    try {
      await sendMessage(form);
      if (version !== sendVersion.current || targetConversation !== conversationRef.current) return;
      followLatestRef.current = true;
      previews.forEach(url => URL.revokeObjectURL?.(url));
      setText("");
      setFiles([]);
      setPreviews([]);
      await refresh(false);
    } catch (err: any) {
      if (version === sendVersion.current && targetConversation === conversationRef.current) setComposeError(err?.response?.data?.message || "메시지를 보내지 못했습니다. 내용을 확인하고 다시 전송해주세요.");
    } finally {
      if (version === sendVersion.current && targetConversation === conversationRef.current) { sendingRef.current = false; setSending(false); }
    }
  };

  const revise = async () => {
    if (readOnly || revisionBusyRef.current || (!editing && !deleting)) return;
    const version = sendVersion.current, target = conversationSeq;
    revisionBusyRef.current = true; setRevisionBusy(true); setRevisionError("");
    try {
      if (editing && editMessage) await editMessage(editing.seq, editing.content.trim(), editing.revision);
      else if (deleting && deleteMessage) await deleteMessage(deleting.seq, deleting.revision_seq || 0);
      if (version !== sendVersion.current || target !== conversationRef.current) return;
      setEditing(null); setDeleting(null); await refresh(false); onActivityRef.current?.();
    } catch (cause: any) {
      if (version === sendVersion.current && target === conversationRef.current) setRevisionError(cause?.response?.data?.message || "답변을 변경하지 못했습니다. 다시 시도해주세요.");
    } finally {
      if (version === sendVersion.current && target === conversationRef.current) { revisionBusyRef.current = false; setRevisionBusy(false); }
    }
  };

  return <ChatShell $seller={variant === "seller"}>
    <Messages ref={messagesRef} aria-label="대화 내용" aria-live="polite" aria-busy={loading} onScroll={event => {
      const container = event.currentTarget;
      if (!loading) {
        if (container.scrollHeight - container.clientHeight - container.scrollTop <= 32) followLatestRef.current = true;
        else if (container.scrollTop < lastScrollTopRef.current) followLatestRef.current = false;
        lastScrollTopRef.current = container.scrollTop;
      }
    }}>
      <MessageContent ref={contentRef}>
      {!loading && messages.length === 0 && summary}
      {loading ? <ContentState compact kind="loading" icon="chat" title="대화를 불러오는 중입니다." />
        : error && messages.length === 0 ? <ContentState compact kind="error" icon="chat" title={error} action={{ label: "다시 시도", onClick: () => { void refresh(true); } }} />
        : messages.length === 0 ? children ? null : readOnly ? <ContentState compact icon="chat" title="채팅 내역이 없습니다." /> : <ContentState compact icon="chat" title="첫 메시지를 보내 상담을 시작해 주세요." description="궁금한 점을 아래 입력창에 남겨주세요." />
        : messages.map((message, index) => {
          const mine = message.sender_role === mineRole;
          const timestamp = new Date(message.created_at);
          const validTime = !Number.isNaN(timestamp.getTime());
          const showDate = variant === "seller" && validTime && (index === 0 || new Date(messages[index - 1].created_at).toDateString() !== timestamp.toDateString());
          return <Fragment key={message.seq}>{showDate && <DateDivider>{timestamp.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", weekday: "short" })}</DateDivider>}{index === 0 && summary}<MessageRow $mine={mine}>
            <Sender>{variant === "seller" ? message.sender_name || (mine ? "담당자" : "고객") : mine ? "나" : message.sender_name || "관리자"}</Sender>
            <Bubble $mine={mine}>
              {message.deleted_at ? <MessageText>삭제된 메시지입니다.</MessageText> : editing?.seq === message.seq ? <MessageEdit>
                <textarea aria-label="답변 수정 내용" value={editing.content} maxLength={2000} disabled={revisionBusy} onChange={event => setEditing({ ...editing, content: event.target.value })} />
                <button type="button" disabled={revisionBusy || !editing.content.trim()} onClick={() => void revise()}>수정 저장</button>
                <button type="button" disabled={revisionBusy} onClick={() => { setEditing(null); setRevisionError(""); }}>수정 취소</button>
              </MessageEdit> : message.content && <MessageText>{message.content}</MessageText>}
              {!message.deleted_at&&mineRole==='customer'&&message.sender_role==='staff'&&message.inquiry_context?.action==='register_refund_account'&&<RefundAccountLink href="/my-info#refund-account">환불 계좌 등록하기</RefundAccountLink>}
              {!message.deleted_at && message.attachments?.length > 0 && <AttachmentGrid $single={message.attachments.length === 1}>
                {message.attachments.map(attachment => isVideo(attachment)
                  ? <video key={attachment.seq} src={attachmentUrl(attachment.file_path)} controls preload="metadata" aria-label={attachment.original_name || "첨부 동영상"} />
                  : <a key={attachment.seq} href={attachmentUrl(attachment.file_path)} target="_blank" rel="noreferrer"><img src={attachmentUrl(attachment.file_path)} alt={attachment.original_name || "첨부 사진"} loading="lazy" /></a>)}
              </AttachmentGrid>}
            </Bubble>
            <Time>{validTime ? new Intl.DateTimeFormat("ko-KR", { hour: "2-digit", minute: "2-digit" }).format(timestamp) : "시간 미등록"}{message.edited_at && !message.deleted_at && " · 수정됨"}</Time>
            {!readOnly && mineRole === "staff" && message.sender_role === "staff" && message.can_edit && !message.deleted_at && <MessageActions>
              {editMessage && <button type="button" aria-label={`${message.seq}번 답변 수정`} disabled={revisionBusy} onClick={() => { setEditing({ seq: message.seq, content: message.content || "", revision: message.revision_seq || 0 }); setDeleting(null); setRevisionError(""); }}>수정</button>}
              {deleteMessage && <button type="button" aria-label={`${message.seq}번 답변 삭제`} disabled={revisionBusy} onClick={() => { setDeleting(message); setEditing(null); setRevisionError(""); }}>삭제</button>}
            </MessageActions>}
            {deleting?.seq === message.seq && <MessageActions role="group" aria-label="답변 삭제 확인"><span>이 답변을 삭제하시겠습니까?</span><button type="button" disabled={revisionBusy} onClick={() => void revise()}>삭제 확인</button><button type="button" disabled={revisionBusy} onClick={() => { setDeleting(null); setRevisionError(""); }}>삭제 취소</button></MessageActions>}
            {revisionError && (editing?.seq === message.seq || deleting?.seq === message.seq) && <InlineError role="alert">{revisionError}</InlineError>}
          </MessageRow></Fragment>;
        })}
      {children}
      {!readOnly && disabledReason && <AssignmentPrompt role="status">{disabledReason}</AssignmentPrompt>}
      {readOnly && error && messages.length > 0 && <ContentState compact kind="error" icon="chat" title={error} action={{ label: "다시 시도", onClick: () => { void refresh(false); } }} />}
      </MessageContent>
    </Messages>

    {!readOnly && <Composer $seller={variant === "seller"} onSubmit={submit}>
      {composerContext}
      {showMacros && <MacroCandidates role="listbox" id={macroListId} aria-label="태그 매크로 답변">
        {macroError ? <InlineError role="alert">{macroError}</InlineError> : matchingMacros.map((macro, index) => <button key={macro.seq} type="button" role="option" id={`${macroListId}-${index}`} aria-selected={index === macroIndex} disabled={macroBusy}
          onMouseDown={event => event.preventDefault()} onClick={() => { void insertMacro(macro); }}>
          <strong>{macro.tags.map(value => `#${value}`).join(" ")} · {macro.title}</strong><span>{macro.content}</span>
        </button>)}
        {!macroError && !matchingMacros.length && <p>일치하는 태그가 없습니다.</p>}
      </MacroCandidates>}
      {previews.length > 0 && <PreviewRow>
        {previews.map((preview, index) => <Preview key={preview}>
          {files[index]?.type.startsWith("video/") ? <video src={preview} muted preload="metadata" aria-label={`전송할 동영상 ${index + 1}`} /> : <img src={preview} alt={`전송할 사진 ${index + 1}`} />}
          <button type="button" disabled={sending} onClick={() => removeFile(index)} aria-label={`첨부파일 ${index + 1} 삭제`}>×</button>
        </Preview>)}
      </PreviewRow>}
      {error && messages.length > 0 && <InlineError role="alert">{error}</InlineError>}
      {composeError && <InlineError role="alert">{composeError}</InlineError>}
      <ComposeRow $seller={variant === "seller"} $action={!!footerAction}>
        <AttachmentControl ref={attachmentRef}>
          {onSelectProducts && attachmentMenu && <AttachmentActions id={attachmentId} aria-label="첨부 선택">
            <button type="button" disabled={sending || files.length >= 5} onClick={() => { if (fileRef.current) { fileRef.current.accept = "image/jpeg,image/png,image/webp"; fileRef.current.click(); } setAttachmentMenu(false); }}>사진첨부</button>
            <button type="button" disabled={sending || files.length >= 5} onClick={() => { if (fileRef.current) { fileRef.current.accept = "video/mp4,video/webm"; fileRef.current.click(); } setAttachmentMenu(false); }}>동영상첨부</button>
            <button type="button" disabled={sending} onClick={() => { setAttachmentMenu(false); onSelectProducts(); }}>문의할 상품 선택</button>
          </AttachmentActions>}
          <AttachButton type="button" onClick={() => onSelectProducts ? setAttachmentMenu(previous => !previous) : fileRef.current?.click()} disabled={sending || !!disabledReason || (!onSelectProducts && files.length >= 5)} aria-expanded={onSelectProducts ? attachmentMenu : undefined} aria-controls={onSelectProducts ? attachmentId : undefined} aria-label={onSelectProducts ? "첨부 메뉴" : "사진 및 동영상 첨부"} title="사진 및 동영상 첨부"><PlusIcon aria-hidden="true" viewBox="0 0 20 20"><path d="M10 3v14M3 10h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></PlusIcon></AttachButton>
        </AttachmentControl>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm" multiple disabled={sending || !!disabledReason} onChange={chooseFiles} hidden />
        <MessageInput ref={inputRef} rows={1} value={text} disabled={sending || macroBusy || !!disabledReason} onChange={(event: ChangeEvent<HTMLTextAreaElement>) => { setText(event.target.value); setCaret(event.target.selectionStart); setDismissedMacro(null); }}
          onSelect={event => setCaret(event.currentTarget.selectionStart)} aria-controls={showMacros ? macroListId : undefined} aria-activedescendant={showMacros && matchingMacros[macroIndex] ? `${macroListId}-${macroIndex}` : undefined}
          onKeyDown={event => {
            if (showMacros && !event.nativeEvent.isComposing && event.keyCode !== 229) {
              if (event.key === "Escape") { event.preventDefault(); setDismissedMacro(macroKey); return; }
              if (matchingMacros.length && ["ArrowDown", "ArrowUp"].includes(event.key)) { event.preventDefault(); setMacroIndex(index => (index + (event.key === "ArrowDown" ? 1 : -1) + matchingMacros.length) % matchingMacros.length); return; }
              if (event.key === "Enter" && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) { event.preventDefault(); if (matchingMacros[macroIndex]) void insertMacro(matchingMacros[macroIndex]); return; }
            }
            handleChatComposerKeyDown(event, setText, () => { void submit(event); });
          }}
          placeholder={disabledReason || "메시지를 입력해주세요."} maxLength={2000} aria-label="메시지" />
        <SendButton $seller={variant === "seller"} type="submit" aria-label={sending ? "전송 중" : "전송"} disabled={sending || macroBusy || !!disabledReason || (!text.trim() && files.length === 0 && !composerContext)}>{variant === "seller" || onSelectProducts ? <img src={onSelectProducts ? inquirySendIcon : sendIcon} width="20" height="20" alt="" /> : sending ? "전송 중" : "전송"}</SendButton>
        {footerAction && <FooterAction>{footerAction}</FooterAction>}
      </ComposeRow>
    </Composer>}
  </ChatShell>;
}

const ChatShell = styled.div<{ $seller:boolean }>`--support-accent:var(--user-button,#6941d9);display:flex;flex-direction:column;min-height:0;height:100%;background:var(--user-surface,#f6f6f6);color:var(--user-text,#222);`;
const AttachmentControl = styled.div`position:relative;`;
const MacroCandidates = styled.div`max-height:240px;overflow:auto;border:1px solid var(--user-border,#ddd);border-radius:6px;margin:8px;padding:4px;background:var(--user-surface,#fff);button{display:flex;flex-direction:column;gap:6px;width:100%;text-align:left;padding:12px;border:0;background:transparent;color:var(--user-text,#222);cursor:pointer;}button[aria-selected="true"]{background:var(--user-surface-raised,#f3f3f3);}strong{font-size:13px;}span{font-size:13px;line-height:1.5;white-space:pre-wrap;}p{padding:8px;font-size:13px;color:var(--user-text-muted,#666);}`;
const AttachmentActions = styled.div`position:absolute;z-index:5;bottom:calc(100% + 12px);left:0;width:190px;display:flex;flex-direction:column;border:1px solid var(--user-border);border-radius:8px;background:var(--user-surface);box-shadow:0 4px 20px #0002;padding:4px;button{min-height:44px;border:0;border-radius:4px;padding:10px 12px;background:var(--user-surface);color:var(--user-text);text-align:left;font:14px var(--font-Pretendard-Regular);cursor:pointer;}button:hover{background:var(--user-surface-raised);}button:disabled{opacity:.5;cursor:default;}button:focus-visible{outline:2px solid var(--user-accent,#3209b9);outline-offset:-2px;}`;
const Messages = styled.div`flex:1;min-height:0;overflow-y:auto;overscroll-behavior-y:contain;overflow-anchor:none;scroll-behavior:auto;padding:20px;box-sizing:border-box;`;
const MessageContent = styled.div`min-height:100%;display:flex;flex-direction:column;gap:16px;`;
const Retry = styled.button`min-height:44px;border:1px solid var(--user-border,#ddd);border-radius:6px;padding:0 18px;background:var(--user-surface-raised,#2d2d2d);color:var(--user-text,#292433);cursor:pointer;`;
const MessageRow = styled.div<{ $mine: boolean }>`display:flex;flex-direction:column;align-items:${({$mine}) => $mine ? "flex-end" : "flex-start"};gap:4px;`;
const Sender = styled.span`font-size:12px;color:var(--user-text-muted,#666);`;
const Bubble = styled.div<{ $mine: boolean }>`box-sizing:border-box;width:fit-content;min-width:0;max-width:min(82%,540px);padding:11px 13px;border-radius:6px;background:${({$mine}) => $mine ? "var(--support-accent)" : "var(--user-surface-raised,#fff)"};color:${({$mine}) => $mine ? "#fff" : "var(--user-text,#222)"};overflow-wrap:anywhere;`;
const MessageText = styled.div`white-space:pre-wrap;font-size:15px;line-height:1.5;`;
const Time = styled.span`font-size:11px;color:var(--user-text-muted,#666);`;
const DateDivider = styled.time`align-self:center;border-radius:14px;background:var(--user-surface-raised);padding:7px 12px;color:var(--user-text-muted);font-size:11px;`;
const AttachmentGrid = styled.div<{ $single: boolean }>`display:grid;width:${({$single}) => $single ? "260px" : "520px"};max-width:100%;grid-template-columns:repeat(${({$single}) => $single ? 1 : 2},minmax(0,1fr));gap:6px;margin-top:6px;a{display:block;min-width:0;}img,video{display:block;width:100%;max-height:260px;aspect-ratio:1;object-fit:cover;border-radius:5px;}`;
const Composer = styled.form<{ $seller: boolean }>`flex:none;border-top:1px solid var(--user-border,#ddd);background:var(--user-composer,#fff);padding:${({$seller})=>$seller ? "0" : "12px 16px max(12px,env(safe-area-inset-bottom))"};${({$seller})=>$seller && "textarea{border:0;border-radius:0;background:transparent;min-height:48px;font-size:14px;}textarea:focus-visible{outline:2px solid var(--user-accent,#3209b9);outline-offset:-2px;}button{min-height:48px;}"}`;
const ComposeRow = styled.div<{ $action: boolean; $seller: boolean }>`display:grid;grid-template-columns:${({$seller})=>$seller ? "48px" : "44px"} minmax(0,1fr) 44px ${({$action})=>$action ? "auto" : ""};gap:${({$seller})=>$seller ? "0" : "4px"};align-items:end;${({$seller})=>$seller && ">div:first-child>button{width:48px;height:48px;border:0;border-radius:0;background:var(--user-surface-raised);display:grid;place-items:center;}"}@media(max-width:600px){grid-template-columns:44px minmax(0,1fr) 44px;}`;
const AttachButton = styled.button`display:grid;place-items:center;padding:0;width:44px;height:44px;box-sizing:border-box;border:1px solid var(--user-border,#ccc);border-radius:6px;background:var(--user-surface-raised,#fff);color:var(--user-text,#333);font-size:25px;cursor:pointer;&:disabled{opacity:.45;cursor:default;}`;
const MessageInput = styled.textarea`display:block;width:100%;min-width:0;height:44px;min-height:44px;max-height:132px;resize:none;overflow-y:hidden;box-sizing:border-box;border:1px solid var(--user-border,#ccc);border-radius:6px;padding:10px 12px;background:var(--user-surface-raised,#fff);color:var(--user-text,#222);font:16px/22px var(--font-Pretendard-Regular);&::placeholder{color:var(--user-text-muted);}&:focus-visible{outline:2px solid var(--user-accent,#6941d9);border-color:var(--user-accent,#6941d9);}`;
const SendButton = styled.button<{ $seller: boolean }>`width:44px;height:44px;border:0;border-radius:4px;padding:0;background:${({$seller})=>$seller ? "transparent" : "var(--support-accent)"};color:#fff;font-family:var(--font-Pretendard-SemiBold);cursor:pointer;img{display:block;width:20px;height:20px;margin:auto;filter:${({$seller})=>$seller ? "var(--admin-theme-icon-filter, none)" : "none"};}&:disabled{opacity:.45;cursor:default;}`;
const PlusIcon = styled.svg`display:block;width:20px;height:20px;flex:none;`;
const AssignmentPrompt = styled.div`align-self:center;margin-top:auto;border-radius:20px;padding:12px 20px;background:#2d2d2d;color:#fff;font-size:12px;`;
const FooterAction = styled.div`align-self:stretch;display:flex;button{min-height:44px;font:inherit;border:0;background:#2d2d2d;color:#fff;padding:0 14px;cursor:pointer;}button:disabled{opacity:.5;cursor:default;}@media(max-width:600px){grid-column:1/-1;justify-content:flex-end;}`;
const PreviewRow = styled.div`display:flex;gap:8px;overflow-x:auto;margin-bottom:10px;`;
const Preview = styled.div`position:relative;flex:0 0 64px;width:64px;height:64px;img,video{width:100%;height:100%;object-fit:cover;border-radius:6px;}button{position:absolute;right:0;top:0;width:24px;height:24px;border:0;border-radius:50%;background:#222;color:#fff;cursor:pointer;}`;
const InlineError = styled.div`color:var(--user-error);font-size:13px;margin-bottom:8px;`;

const MessageActions = styled.div`display:flex;align-items:center;flex-wrap:wrap;gap:8px;font-size:12px;button{min-height:32px;padding:4px 8px;border:1px solid var(--user-border,#ddd);border-radius:4px;background:var(--user-surface,#fff);color:var(--user-text,#222);cursor:pointer;}`;
const MessageEdit = styled.div`display:flex;flex-wrap:wrap;gap:6px;textarea{box-sizing:border-box;width:100%;min-width:180px;min-height:90px;resize:vertical;padding:8px;border:1px solid var(--user-border,#ddd);border-radius:4px;background:var(--user-surface,#fff);color:var(--user-text,#222);font:inherit;}button{min-height:32px;cursor:pointer;}`;

const RefundAccountLink=styled.a`display:flex;align-items:center;justify-content:center;box-sizing:border-box;min-height:44px;margin-top:12px;padding:10px 14px;border:1px solid var(--user-accent);border-radius:6px;background:var(--user-accent-soft);color:var(--user-accent);font:inherit;font-weight:600;text-decoration:none;&:focus-visible{outline:2px solid var(--user-accent);outline-offset:2px;}`;
